interface Listener<T extends any[]> {
  (...args: T): void

  // Property used to keep the original identity of listeners.
  _?: Listener<T>
}

export class EventEmitter<
  Events extends Record<string, any[]> = Record<string, any[]>
> {
  private listeners: {
    [E in keyof Events]?: Array<Listener<Events[E]>>
  } = {}

  /**
   * Listens to an event once.
   *
   * @param name Name of the event to listen to.
   * @param listener The function to call when the event is emitted.
   */
  public on<E extends keyof Events, T>(
    name: E,
    listener: (this: T, ...args: Events[E]) => void,
    thisArg?: T
  ) {
    let listeners = this.listeners[name]
    if (!listeners) {
      listeners = this.listeners[name] = []
    }
    if (thisArg) {
      const boundListener: Listener<Events[E]> = listener.bind(thisArg)
      // Record the original identity of the listener so that off can still work.
      boundListener._ = listener
      listeners.push(boundListener)
    } else {
      listeners.push(listener)
    }
  }

  /**
   * Listens to an event only once.
   *
   * @param name Name of the event to listen to.
   * @param listener the function to call when the event is emitted.
   */
  public once<E extends keyof Events, T>(
    name: E,
    listener: (this: T, ...args: Events[E]) => void,
    thisArg?: T
  ) {
    const onceListener = (...args: Events[E]) => {
      this.off(name, onceListener)
      listener.apply(thisArg, args)
    }
    // Record the original identity of the listener so that off can still work.
    onceListener._ = listener

    return this.on(name, onceListener)
  }

  /**
   * Trigger a named event.
   *
   * @param name Name of the event to trigger.
   * @param data Data to pass to event listeners.
   */
  public emit<E extends keyof Events>(name: E, ...data: Events[E]) {
    const listeners = this.listeners[name]
    if (!listeners) {
      return
    }

    listeners.forEach(listener => {
      listener(...data)
    })
  }

  /**
   * Stop listening to a named event.
   *
   * @param name The name of the event to stop listening to.
   * @param listener The function used when binding to the event.
   *                 If unspecified, all listeners are removed.
   */
  public off<E extends keyof Events>(
    name: E,
    listener?: (...args: Events[E]) => void
  ) {
    const listeners = this.listeners[name]
    if (!listeners) {
      return
    }

    const newListeners = listener
      ? listeners.filter(l => l !== listener && l._ !== listener)
      : []
    if (newListeners.length) {
      this.listeners[name] = newListeners
    } else {
      delete this.listeners[name]
    }
  }
}
